import type { BoxProps, IconProps, TableCellProps, TextProps } from "@chakra-ui/react";
import { useBreakpointValue, useColorMode } from "@chakra-ui/react";
import { useColorModeValue } from "@chakra-ui/react";
import { useMemo } from "react";

type ButtonType = "primary" | "secondary" | "cta" | "subscribe" | "delete" | "submit";

// Export buttonStyles for backward compatibility
export const buttonStyles = {
  primary: {
    bgColor: "gray.900",
    color: "primary.default",
    borderColor: "primary.default",
    hoverBgColor: "gray.800",
    hoverBorderColor: "primary.default",
  },
  secondary: {
    bgColor: "white",
    color: "gray.500",
    hoverBorderColor: "gray.400",
    hoverBgColor: "gray.50",
    borderColor: "gray.300",
  },
  cta: {
    bgColor: "primary.default",
    color: "white",
    hoverBorderColor: "#318baf",
    hoverBgColor: "#318baf",
    borderColor: "primary.default",
  },
  subscribe: {
    bgColor: "#fdb516",
    color: "gray.700",
    hoverBorderColor: "#ffcc5a",
    hoverBgColor: "#ffcc5a",
    borderColor: "#fdb516",
  },
};

const getButtonStyles = (colorMode: "light" | "dark") => ({
  primary: {
    bgColor: colorMode === "dark" ? "transparent" : "white",
    color: "primary.default",
    borderColor: "primary.default",
    hoverBgColor: colorMode === "dark" ? "gray.700" : "gray.100",
    hoverBorderColor: "primary.default",
  },
  secondary: {
    bgColor: colorMode === "dark" ? "gray.700" : "white",
    color: colorMode === "dark" ? "gray.300" : "gray.500",
    hoverBorderColor: colorMode === "dark" ? "gray.600" : "gray.50",
    hoverBgColor: colorMode === "dark" ? "gray.800" : "gray.50",
    borderColor: colorMode === "dark" ? "gray.700" : "gray.300",
  },
  delete: {
    bgColor: colorMode === "dark" ? "gray.900" : "white",
    color: colorMode === "dark" ? "red.300" : "red.500",
    hoverBorderColor: colorMode === "dark" ? "red.600" : "red.50",
    hoverBgColor: colorMode === "dark" ? "red.800" : "red.50",
    borderColor: colorMode === "dark" ? "red.700" : "red.300",
  },
  submit: {
    bgColor: colorMode === "dark" ? "yellow.600" : "#81c34b",
    color: colorMode === "dark" ? "gray.900" : "white",
    hoverBorderColor: colorMode === "dark" ? "yellow.500" : "#81c34b",
    hoverBgColor: colorMode === "dark" ? "yellow.500" : "#81c34b",
    borderColor: colorMode === "dark" ? "yellow.600" : "#81c34b",
  },
  cta: {
    bgColor: "primary.default",
    color: colorMode === "dark" ? "gray.900" : "gray.50",
    hoverBorderColor: colorMode === "dark" ? "#0f90c4" : "#81c34c",
    hoverBgColor: colorMode === "dark" ? "#0f90c4" : "#81c34c",
    borderColor: "primary.default",
  },
  subscribe: {
    bgColor: colorMode === "dark" ? "yellow.600" : "#fdb516",
    color: colorMode === "dark" ? "gray.900" : "gray.700",
    hoverBorderColor: colorMode === "dark" ? "yellow.500" : "#ffcc5a",
    hoverBgColor: colorMode === "dark" ? "yellow.500" : "#ffcc5a",
    borderColor: colorMode === "dark" ? "yellow.600" : "#fdb516",
  },
});

export const useButtonProps = (size?: string, type: ButtonType = "primary", showBoxShadow?: false) => {
  const { colorMode } = useColorMode();
  const buttonStyles = getButtonStyles(colorMode);
  const { bgColor, color, hoverBorderColor, hoverBgColor, borderColor } = buttonStyles[type];

  return useMemo(
    () => ({
      size: size ?? "sm",
      backgroundColor: bgColor,
      color,
      borderRadius: "md",
      borderColor,
      variant: "outline",
      _active: {},
      _hover: { backgroundColor: hoverBgColor, borderColor: hoverBorderColor, boxShadow: showBoxShadow ? "md" : "none" },
    }),
    [size, bgColor, color, borderColor, hoverBorderColor, hoverBgColor, showBoxShadow]
  );
};

export const useLinkActionProps = (size?: string) => {
  const bgColor = useColorModeValue("primary.default", "gray.800");
  const borderColor = useColorModeValue("primary.default", "primary.default");
  const color = useColorModeValue("white", "primary.default");
  const borderWidth = useColorModeValue("1px", "2px");

  const commonLinkActionProps = useMemo(
    () => ({
      justifyContent: "left",
      size: size ?? "sm",
      fontWeight: "400",
      backgroundColor: bgColor,
      color: color,
      borderRadius: "full",
      borderColor: borderColor,
      borderWidth: borderWidth,
      boxShadow: "xl",
      _active: {},
      _hover: {},
    }),
    [size, bgColor, color, borderColor, borderWidth]
  );

  return commonLinkActionProps;
};

export const useTabProps = () => {
  const tabButtonColor = useColorModeValue("gray.500", "gray.400");
  const linkColor = useColorModeValue("gray.600", "gray.400");
  const tabHoverColor = useColorModeValue("secondaryButton.hover", "secondaryButton.hoverDarkMode");
  const isMobile = useBreakpointValue({ base: true, md: false }, { fallback: "md", ssr: false });
  const borderColor = useColorModeValue("gray.200", "gray.700");

  const commonTabProps = useMemo(() => {
    return {
      fontSize: ["sm", "md", "md"],
      fontWeight: "normal",
      color: tabButtonColor,
      justifyContent: "left",
      border: "none",
      _selected: { borderColor: linkColor, borderBottom: "1px solid", color: tabHoverColor, fontWeight: "bold" },
      _hover: {
        color: tabHoverColor,
        borderColor: tabHoverColor,
      },
    };
  }, [tabButtonColor, linkColor, tabHoverColor]);

  const defaultTabProps = useMemo(() => {
    return {
      fontSize: isMobile ? "sm" : "md",
      width: isMobile ? "unset" : "12rem",
      justifyContent: isMobile ? "center" : "flex-start",
      borderColor: borderColor,
      mr: isMobile ? "0" : "1rem",
      color: linkColor,
      _selected: { fontWeight: "bold", color: "primary.default", borderColor: "primary.default" },
      _active: {},
      _hover: {
        color: tabHoverColor,
      },
    };
  }, [isMobile, borderColor, linkColor, tabHoverColor]);

  return { commonTabProps, defaultTabProps };
};

export const useIconProps = () => {
  const buttonColor = useColorModeValue("gray.500", "gray.400");
  const buttonBgColor = useColorModeValue("white", "gray.800");
  const hoverColor = useColorModeValue("primary.default", "primary.300");
  const hoverBgColor = useColorModeValue("white", "gray.700");

  const commonIconProps = useMemo(() => {
    return {
      fontWeight: "300",
      "aria-label": "icon",
      backgroundColor: buttonBgColor,
      color: buttonColor,
      cursor: "pointer",
      boxSize: "1.4rem",
      _hover: {
        color: hoverColor,
        borderColor: buttonColor,
        backgroundColor: hoverBgColor,
      },
    };
  }, [buttonColor, buttonBgColor, hoverColor, hoverBgColor]);

  return commonIconProps as IconProps;
};

export const useTileProps = () => {
  const bgColor = useColorModeValue("white", "#161B25");
  const tileBorderColor = useColorModeValue("gray.300", "gray.700");
  const borderColorHover = useColorModeValue("gray.300", "gray.600");

  const commonTileProps = useMemo(() => {
    return {
      position: "relative",
      cursor: "pointer",
      backgroundColor: bgColor,
      borderColor: tileBorderColor,
      borderWidth: "1px",
      borderRadius: "lg",
      px: "1rem",
      py: ".5rem",
      width: "100%",
      height: "100%",
      overflow: "hidden",
      whiteSpace: "normal",
      _hover: {
        borderColor: borderColorHover,
      },
    };
  }, [bgColor, borderColorHover, tileBorderColor]);

  return commonTileProps as BoxProps;
};

export const useMetricProps = () => {
  const titleColor = useColorModeValue("charli.lightGray", "gray.500");
  const commonMetricProps: TextProps = {
    isTruncated: true,
    fontWeight: "light",
    color: titleColor,
    textAlign: "center",
  };

  return commonMetricProps;
};

export const useTableProps = () => {
  const color = useColorModeValue("charli.lightGray", "gray.500");
  const commonTableTdProps: TableCellProps = {
    border: "none",
    color: color,
  };

  return commonTableTdProps;
};

export const useSwitchProps = () => {
  const trackBg = useColorModeValue("gray.200", "gray.700");
  const checkedTrackBg = useColorModeValue("primary.500", "gray.700");
  const thumbColor = useColorModeValue("white", "gray.600");
  const checkedThumbColor = useColorModeValue("white", "gray.800");

  return useMemo(
    () => ({
      sx: {
        "& .chakra-switch__track": {
          bg: trackBg,
          _checked: {
            bg: checkedTrackBg,
          },
        },
        "& .chakra-switch__thumb": {
          bg: thumbColor,
          _checked: {
            bg: checkedThumbColor,
          },
        },
      },
    }),
    [trackBg, checkedTrackBg, thumbColor, checkedThumbColor]
  );
};

export const useSkeletonProps = () => {
  const startColor = useColorModeValue("gray.100", "gray.900");
  const endColor = useColorModeValue("gray.50", "gray.800");

  const commonSkeletonProps = useMemo(() => {
    return {
      startColor: startColor,
      endColor: endColor,
      borderRadius: "md",
    };
  }, [startColor, endColor]);

  return commonSkeletonProps;
};
